/**
 * Secure Random Number Generation with Entropy Validation
 * Follows NIST SP 800-90B guidelines - uses single trusted source
 * Addresses High Severity Finding #2 from Security Audit
 */

import { sha256 } from '@noble/hashes/sha256'
import { logSecurityEvent, SecurityEventType } from '../monitoring/security-monitor'

interface PlatformCapabilities {
  hasWebCrypto: boolean
  hasGetRandomValues: boolean
  supportsSubtleCrypto: boolean
  isSecureContext: boolean
  browserInfo: string
}

interface EntropyQuality {
  score: number
  tests: {
    frequency: number
    runs: number
    autocorrelation: number
  }
  platform: string
}

export class SecureRandom {
  private static platformCaps: PlatformCapabilities | null = null
  private static isInitialized = false
  private static entropyQuality: EntropyQuality | null = null
  
  /**
   * Initialize and validate platform entropy capabilities
   * Best practice: Use single trusted source (Web Crypto API)
   */
  static async initialize(): Promise<void> {
    if (this.isInitialized) return
    
    // Assess platform capabilities
    this.platformCaps = this.assessPlatform()
    
    // Validate we have secure entropy source
    if (!this.platformCaps.hasWebCrypto || !this.platformCaps.hasGetRandomValues) {
      const error = 'Platform lacks secure entropy source (Web Crypto API unavailable)'
      logSecurityEvent(
        SecurityEventType.ENTROPY_FAILURE,
        'critical',
        { 
          platform: this.platformCaps,
          error,
          fallbackAvailable: false
        },
        'SecureRandom'
      )
      throw new Error(error)
    }
    
    // Test entropy quality from platform source
    this.entropyQuality = await this.validatePlatformEntropy()
    
    if (this.entropyQuality.score < 0.8) {
      logSecurityEvent(
        SecurityEventType.LOW_ENTROPY,
        this.entropyQuality.score < 0.5 ? 'critical' : 'high',
        { 
          entropyQuality: this.entropyQuality,
          platform: this.platformCaps.browserInfo
        },
        'SecureRandom'
      )
      
      // Decision point for old browsers/hardware
      if (this.entropyQuality.score < 0.5) {
        throw new Error(`Platform entropy quality too low for secure operations (score: ${this.entropyQuality.score})`)
      }
    }
    
    this.isInitialized = true
    
    // Log successful initialization
    logSecurityEvent(
      SecurityEventType.SECURITY_INITIALIZED,
      'low',
      { 
        entropyQuality: this.entropyQuality.score,
        platform: this.platformCaps.browserInfo
      },
      'SecureRandom'
    )
  }
  
  /**
   * Get cryptographically secure random bytes
   * Uses only Web Crypto API as single trusted source
   */
  static async getSecureBytes(length: number): Promise<Uint8Array> {
    if (!this.isInitialized) {
      await this.initialize()
    }
    
    // Final entropy check before generation
    if (!this.hasGoodEntropy()) {
      logSecurityEvent(
        SecurityEventType.ENTROPY_FAILURE,
        'critical',
        { reason: 'Failed final entropy check before generation' },
        'SecureRandom'
      )
      throw new Error('Insufficient entropy for secure byte generation')
    }
    
    // Use Web Crypto API directly - single trusted source
    const bytes = new Uint8Array(length)
    crypto.getRandomValues(bytes)
    
    return bytes
  }
  
  /**
   * Assess platform cryptographic capabilities
   */
  private static assessPlatform(): PlatformCapabilities {
    const hasWebCrypto = typeof crypto !== 'undefined' && crypto !== null
    const hasGetRandomValues = hasWebCrypto && typeof crypto.getRandomValues === 'function'
    const supportsSubtleCrypto = hasWebCrypto && typeof crypto.subtle !== 'undefined'
    const isSecureContext = typeof window !== 'undefined' && window.isSecureContext
    
    // Browser detection for logging
    let browserInfo = 'unknown'
    if (typeof navigator !== 'undefined') {
      browserInfo = navigator.userAgent || 'unknown-navigator'
    }
    
    return {
      hasWebCrypto,
      hasGetRandomValues,
      supportsSubtleCrypto,
      isSecureContext,
      browserInfo
    }
  }
  
  /**
   * Validate entropy quality from platform source
   * Tests Web Crypto API output quality
   */
  private static async validatePlatformEntropy(): Promise<EntropyQuality> {
    const testSize = 1000 // 1KB test sample
    const testData = new Uint8Array(testSize)
    crypto.getRandomValues(testData)
    
    // Run statistical tests on platform entropy
    const frequency = this.frequencyTest(testData)
    const runs = this.runsTest(testData)
    const autocorrelation = this.autocorrelationTest(testData)
    
    // Calculate composite score
    const score = (frequency + runs + autocorrelation) / 3
    
    return {
      score,
      tests: { frequency, runs, autocorrelation },
      platform: this.platformCaps?.browserInfo || 'unknown'
    }
  }
  
  /**
   * Frequency (monobit) test
   * Tests for equal distribution of 0s and 1s
   */
  private static frequencyTest(data: Uint8Array): number {
    let ones = 0
    for (const byte of data) {
      for (let i = 0; i < 8; i++) {
        if ((byte >> i) & 1) ones++
      }
    }
    
    const totalBits = data.length * 8
    const zeros = totalBits - ones
    const ratio = ones / totalBits
    
    // Ideal ratio is 0.5, score based on deviation
    const deviation = Math.abs(ratio - 0.5)
    return Math.max(0, 1 - (deviation * 10)) // Scale deviation
  }
  
  /**
   * Runs test
   * Tests for randomness in sequences
   */
  private static runsTest(data: Uint8Array): number {
    let runs = 1
    let currentBit = data[0] & 1
    
    for (let i = 0; i < data.length; i++) {
      for (let j = 0; j < 8; j++) {
        const bit = (data[i] >> j) & 1
        if (bit !== currentBit) {
          runs++
          currentBit = bit
        }
      }
    }
    
    const totalBits = data.length * 8
    const expectedRuns = totalBits / 2
    const deviation = Math.abs(runs - expectedRuns) / expectedRuns
    
    return Math.max(0, 1 - deviation)
  }
  
  /**
   * Autocorrelation test
   * Tests for independence between values
   */
  private static autocorrelationTest(data: Uint8Array): number {
    if (data.length < 2) return 1
    
    let correlation = 0
    for (let i = 0; i < data.length - 1; i++) {
      correlation += data[i] * data[i + 1]
    }
    
    correlation /= (data.length - 1)
    const expected = 127.5 * 127.5 // Expected for uniform random bytes
    const deviation = Math.abs(correlation - expected) / expected
    
    return Math.max(0, 1 - deviation)
  }
  
  /**
   * Check if platform entropy is acceptable
   */
  private static hasGoodEntropy(): boolean {
    if (!this.entropyQuality) return false
    return this.entropyQuality.score >= 0.7
  }
  
  /**
   * Get current entropy quality assessment
   */
  static getEntropyQuality(): EntropyQuality | null {
    return this.entropyQuality
  }
  
  /**
   * Get platform capabilities
   */
  static getPlatformCapabilities(): PlatformCapabilities | null {
    return this.platformCaps
  }
  
  /**
   * Handle low entropy scenarios
   * Provides guidance for old browsers/hardware
   */
  static getEntropyGuidance(): string {
    if (!this.platformCaps) return 'Platform not assessed'
    
    if (!this.platformCaps.hasWebCrypto) {
      return 'CRITICAL: Web Crypto API unavailable. Upgrade browser or use supported platform.'
    }
    
    if (!this.platformCaps.isSecureContext) {
      return 'WARNING: Not in secure context (HTTPS). Entropy may be limited.'
    }
    
    if (this.entropyQuality && this.entropyQuality.score < 0.5) {
      return 'CRITICAL: Platform entropy quality insufficient. Consider hardware RNG or platform upgrade.'
    }
    
    if (this.entropyQuality && this.entropyQuality.score < 0.8) {
      return 'WARNING: Platform entropy quality marginal. Monitor for issues.'
    }
    
    return 'Platform entropy acceptable for cryptographic operations.'
  }
}